import React from 'react';
import type { UptimePayload, UptimeWindow } from './useApiObserve';

type Props = {
  data: (UptimePayload & { ok?: boolean }) | undefined;
  isLoading: boolean;
  window: UptimeWindow;
  onWindowChange: (w: UptimeWindow) => void;
};

const WINDOWS: UptimeWindow[] = ['24h', '7d', '30d'];

export default function UptimeTimeline({ data, isLoading, window, onWindowChange }: Props) {
  const segments = data?.segments || [];

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {WINDOWS.map((w) => (
          <button
            key={w}
            type="button"
            onClick={() => onWindowChange(w)}
            className={`px-3 py-1 text-sm rounded-lg border ${
              window === w ? 'bg-indigo-600 text-white border-indigo-600' : 'border-gray-300 text-gray-700'
            }`}
          >
            {w}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-500">Loading uptime…</div>
      ) : !data?.has_data ? (
        <div className="text-sm text-gray-500 rounded-lg border border-dashed p-6 text-center">
          No events in this window — uptime cannot be calculated.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-3xl font-bold text-gray-900">{data.uptime_pct}%</div>
              <div className="text-xs text-gray-500">uptime</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-green-700">{data.total_up_minutes}m</div>
              <div className="text-xs text-gray-500">up</div>
            </div>
            <div>
              <div className="text-lg font-semibold text-red-700">{data.total_down_minutes}m</div>
              <div className="text-xs text-gray-500">down</div>
            </div>
          </div>

          <div className="w-full h-8 flex rounded overflow-hidden border border-gray-200">
            {segments.map((seg, i) => (
              <div
                key={`${seg.start}-${i}`}
                title={`${new Date(seg.start).toLocaleString()} – ${new Date(seg.end).toLocaleString()} (${seg.status})`}
                className={seg.status === 'up' ? 'bg-green-500' : 'bg-red-500'}
                style={{ flex: 1, minWidth: 1 }}
              />
            ))}
          </div>

          {segments.length > 0 && (
            <div className="flex justify-between text-xs text-gray-500">
              <span>{new Date(segments[0].start).toLocaleString()}</span>
              <span>
                {segments.length > 1
                  ? new Date(segments[Math.floor(segments.length / 2)].start).toLocaleString()
                  : ''}
              </span>
              <span>{new Date(segments[segments.length - 1].end).toLocaleString()}</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
              <div className="text-xs font-medium text-gray-500 uppercase">Longest outage</div>
              {data.longest_outage ? (
                <div className="mt-1 text-sm text-gray-800">
                  {data.longest_outage.duration_minutes} min
                  <div className="text-xs text-gray-500">
                    {new Date(data.longest_outage.start).toLocaleString()} →{' '}
                    {new Date(data.longest_outage.end).toLocaleString()}
                  </div>
                </div>
              ) : (
                <div className="mt-1 text-sm text-gray-500">None in window</div>
              )}
            </div>
            <div className="rounded-lg border border-gray-200 p-3 bg-gray-50">
              <div className="text-xs font-medium text-gray-500 uppercase">Last incident</div>
              {data.last_incident ? (
                <div className="mt-1 text-sm text-gray-800">
                  {data.last_incident.duration_minutes} min
                  <div className="text-xs text-gray-500">
                    {new Date(data.last_incident.start).toLocaleString()} →{' '}
                    {new Date(data.last_incident.end).toLocaleString()}
                  </div>
                </div>
              ) : (
                <div className="mt-1 text-sm text-gray-500">None in window</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
